import { obtenirUrlControleur } from '../utils/urlUtils';
import fetch from '../utils/fetch';
import { jsonHeaders } from '../utils/fetch';
import Ressource from '../modeles/Ressource';
import RessourceRequest from '../modeles/RessourceRequest';
import { RessourceDto } from '../modeles/RessourceDto';

const lienRessource = obtenirUrlControleur('ressource');

const normaliserRessource = (data: RessourceDto) => new Ressource({
  id: data.id,
  titre: data.titre,
  description: data.description,
  resume: data.resume ?? '',
  idImage: data.idImage,
  categorie: data.categorie,
  cheminFichier: data.cheminFichier,
  image: data.cheminFichier || null,
  estActif: data.estActif,
});

export default class RessourceApi {
  static async obtenirToutesLesRessources(): Promise<Ressource[]> {
    const data = await fetch(`${lienRessource}/api/ressource/ressources`, { method: 'GET' }) as RessourceDto[];
    return Array.isArray(data) ? data.map(normaliserRessource) : [];
  }

  static async obtenirRessourcesParCategorie(categorie: string): Promise<Ressource[]> {
    if (!categorie) return [];

    const data = await fetch(`${lienRessource}/api/ressource/categorie/${encodeURIComponent(categorie)}`, { method: 'GET' }) as RessourceDto[];

    return Array.isArray(data) ? data.map(normaliserRessource) : [];
  }

  static async obtenirRessourceParId(id: string): Promise<Ressource | null> {
    if (!id) return null;

    const data = await fetch(`${lienRessource}/api/ressource/ressourcecourante/${encodeURIComponent(id)}`, { method: 'GET' }) as RessourceDto;
    if (!data || Array.isArray(data)) return null;
    return normaliserRessource(data);
  }

  static async ajouter(ressource: Record<string, unknown>): Promise<Ressource | null> {
    const request = new RessourceRequest({
      titre: String(ressource?.titre ?? ressource?.Titre ?? ''),
      description: String(ressource?.description ?? ressource?.Description ?? ressource?.contenu ?? ressource?.Contenu ?? ''),
      resume: String(ressource?.resume ?? ressource?.Resume ?? ''),
      categorie: String(ressource?.categorie ?? ressource?.Categorie ?? ''),
      estActif: Boolean(ressource?.estActif ?? ressource?.EstActif ?? true),
      fichier: (ressource?.fichier ?? ressource?.Fichier ?? null) as File | null,
      fichiers: (ressource?.fichiers ?? ressource?.Fichiers ?? []) as File[],
    });

    const data = await fetch(`${lienRessource}/api/ressource/ajouter`, {
      method: 'POST',
      body: request.toFormData(),
    }) as RessourceDto;
    return data ? normaliserRessource(data) : null;
  }

  static async ajouterRessource(ressource: Record<string, unknown>): Promise<Ressource | null> {
    return RessourceApi.ajouter(ressource);
  }

  static async modifier(id: string, ressource: Record<string, unknown>): Promise<Ressource | null> {
    if (!id) {
      throw new Error('Id de ressource manquant');
    }

    const payload = new Ressource({
      id,
      titre: (ressource?.titre ?? ressource?.Titre ?? '') as string,
      description: (ressource?.description ?? ressource?.Description ?? '') as string,
      resume: (ressource?.resume ?? ressource?.Resume ?? '') as string,
      idImage: (ressource?.idImage ?? ressource?.IdImage ?? '') as string,
      image: (ressource?.image ?? ressource?.Image ?? null) as string | null,
      categorie: (ressource?.categorie ?? ressource?.Categorie ?? '') as string,
      cheminFichier: (ressource?.cheminFichier ?? ressource?.CheminFichier ?? '') as string,
      estActif: (ressource?.estActif ?? ressource?.EstActif ?? true) as boolean,
    });

    const data = await fetch(`${lienRessource}/api/ressource/modifier`, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(payload.toDto()),
    }) as RessourceDto;
    return data ? normaliserRessource(data) : null;
  }

  static async supprimer(id: string): Promise<void> {
    if (!id) {
      throw new Error('Id de ressource manquant');
    }

    try {
      await fetch(`${lienRessource}/api/ressource/supprimer/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    } catch (error) {
      await fetch(`${lienRessource}/api/ressource/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    }
  }
}